import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, type ApplicationAction, type ApplicationDetail, type ApplicationEvent, type Board, type Candidate } from "../lib/api";
import { openDownload } from "../lib/upload";
import { todayInLondon } from "../lib/dates";
import { useTitle } from "../lib/title";
import { Interviews } from "./Interviews";

const when = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
// Start dates are calendar days, so format them in UTC to stop the viewer's timezone shifting them.
const startDay = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const startFull = new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
export const EVENT_MARK: Record<ApplicationEvent["kind"], string> = {
  added: "+",
  moved: "→",
  feedback: "✎",
  rejected: "×",
  withdrawn: "←",
  reopened: "↺",
  placed: "★",
  unplaced: "↶",
  client_feedback: "✉",
  interview_booked: "◷",
  interview_moved: "◷",
  interview_cancelled: "⊘",
};
const money = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);

export function describeEvent(e: Pick<ApplicationEvent, "kind" | "fromStage" | "toStage">): string {
  switch (e.kind) {
    case "added":
      return "Added to the shortlist";
    case "moved":
      return `Moved from ${e.fromStage} to ${e.toStage}`;
    case "feedback":
      return `Feedback${e.toStage ? ` at ${e.toStage}` : ""}`;
    case "rejected":
      return `Rejected${e.fromStage ? ` at ${e.fromStage}` : ""}`;
    case "withdrawn":
      return `Withdrew${e.fromStage ? ` at ${e.fromStage}` : ""}`;
    case "reopened":
      return "Back in process";
    case "client_feedback":
      return "Client's answer";
    case "interview_booked":
      return "Interview booked";
    case "interview_moved":
      return "Interview moved";
    case "interview_cancelled":
      return "Interview cancelled";
    case "placed":
      return "Placed";
    case "unplaced":
      return "Placement undone";
  }
}

/** One candidate on one job: stage, history, feedback, decisions, and the placement. */
export function ApplicationPage() {
  const { id = "" } = useParams();
  const [detail, setDetail] = useState<ApplicationDetail | null>(null);
  useTitle(detail ? `${detail.application.candidateName} · ${detail.application.jobTitle}` : "Application");
  const [stages, setStages] = useState<Board["stages"]>([]);
  // Contact details, so a call or email doesn't mean leaving the page.
  const [contact, setContact] = useState<Pick<Candidate, "email" | "phone" | "cv"> | null>(null);
  const load = useAction();
  const act = useAction();

  useEffect(() => {
    void load.run(async () => {
      const d = await api.application(id);
      setDetail(d);
      const [board, candidate] = await Promise.all([api.pipeline(d.application.jobId), api.candidate(d.application.candidateId)]);
      setStages(board.stages);
      setContact(candidate.candidate);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!detail) {
    return (
      <>
        <p className="label">
          <Link to="/jobs">← Jobs</Link>
        </p>
        <ErrorNotice error={load.error} />
        {!load.error && <p className="label">Loading…</p>}
      </>
    );
  }

  const { application: app, events, placement } = detail;
  const open = !app.candidateErased;
  const run = (action: ApplicationAction) => void act.run(async () => setDetail(await api.applicationAction(app.id, action)));

  return (
    <>
      <div className="page-head">
        <p className="label">
          <Link to={`/jobs/${app.jobId}`}>← {app.jobTitle}</Link> · {app.companyName}
        </p>
        <h1 className="display">
          <Link className="plain" to={`/candidates/${app.candidateId}`}>
            {app.candidateName}
          </Link>
        </h1>
        <p className="lede">
          {app.status === "active" && (
            <>
              At <strong>{app.stageName}</strong>
            </>
          )}
          {app.status === "placed" && <>Placed</>}
          {(app.status === "rejected" || app.status === "withdrawn") && (
            <>
              {app.status === "rejected" ? "Rejected" : "Withdrew"} at {app.stageName}
            </>
          )}
        </p>
        {contact && !app.candidateErased && (
          <p className="button-row contact-row">
            {contact.email && <a href={`mailto:${contact.email}`}>{contact.email}</a>}
            {contact.phone && (
              <a className="figures" href={`tel:${contact.phone.replace(/[^\d+]/g, "")}`}>
                {contact.phone}
              </a>
            )}
            {contact.cv && (
              <button type="button" className="link-btn" onClick={() => void act.run(() => openDownload(contact.cv!.id))}>
                Download CV
              </button>
            )}
            <Link className="link-btn" to={`/candidates/${app.candidateId}`}>
              Full profile
            </Link>
          </p>
        )}
      </div>
      <ErrorNotice error={act.error} />

      {app.candidateErased && (
        <p className="notice">This person was erased. The history and any placement are kept for your records; nothing more can be added.</p>
      )}

      {open && app.status === "active" && (
        <section className="section" aria-labelledby="stage-h">
          <div className="section-head">
            <h2 id="stage-h" className="label">
              Stage
            </h2>
          </div>
          <fieldset className="segmented spaced">
            <legend className="sr-only">Move to stage</legend>
            {[{ id: null as string | null, name: "Shortlisted" }, ...stages].map((s) => (
              <label key={s.id ?? "shortlisted"}>
                <input
                  type="radio"
                  name="stage"
                  checked={app.stageId === s.id}
                  disabled={act.pending}
                  onChange={() => run({ kind: "move", stageId: s.id })}
                />
                <span>{s.name}</span>
              </label>
            ))}
          </fieldset>
          <CloseOut pending={act.pending} onDone={(kind, note) => run({ kind, note: note || undefined })} />
        </section>
      )}

      {open && (
        <Interviews
          applicationId={app.id}
          canBook={app.status === "active"}
          onChanged={() => void act.run(async () => setDetail(await api.application(app.id)))}
        />
      )}

      {open && (app.status === "rejected" || app.status === "withdrawn") && (
        <section className="section">
          <button type="button" className="btn secondary" disabled={act.pending} onClick={() => run({ kind: "reopen" })}>
            Put back in process
          </button>
        </section>
      )}

      {open && app.status === "active" && <PlaceForm onPlace={(a) => run(a)} pending={act.pending} />}

      {placement && (
        <section className="section" aria-labelledby="placement-h">
          <div className="section-head">
            <h2 id="placement-h" className="label">
              Placement
            </h2>
          </div>
          <dl className="ledger">
            <div>
              <dt className="label">Fee due</dt>
              <dd className="figure-xl">{money(placement.feeAmount, placement.currency)}</dd>
              <dd className="secondary">
                {placement.fee.type === "percent" ? `${placement.fee.percent}% of ${money(placement.salary, placement.currency)}` : "fixed fee"}
              </dd>
            </div>
            <div>
              <dt className="label">Starts</dt>
              <dd className="figure-xl">{startDay.format(new Date(`${placement.startDate}T00:00:00Z`))}</dd>
              <dd className="secondary">{startFull.format(new Date(`${placement.startDate}T00:00:00Z`))}</dd>
            </div>
          </dl>
          <button type="button" className="link-btn danger spaced-top" disabled={act.pending} onClick={() => run({ kind: "unplace" })}>
            Undo placement
          </button>
        </section>
      )}

      {open && <Feedback onAdd={(note) => run({ kind: "feedback", note })} pending={act.pending} />}

      <section className="section" aria-labelledby="history-h">
        <div className="section-head">
          <h2 id="history-h" className="label">
            History
          </h2>
        </div>
        <ol className="index timeline">
          {events.map((e) => (
            <li key={e.id}>
              <span className="num kind" aria-hidden>
                {EVENT_MARK[e.kind]}
              </span>
              <span className="main">
                <span className="primary">{describeEvent(e)}</span>
                {e.note && <span className="prewrap">{e.note}</span>}
                <span className="secondary">
                  {e.by ?? "a former teammate"} · <span className="figures">{when.format(new Date(e.at))}</span>
                </span>
              </span>
              <span className="actions" />
            </li>
          ))}
        </ol>
      </section>
    </>
  );
}

/** Reject or record a withdrawal: an optional reason, then confirm (no one-click mistakes). */
function CloseOut({ pending, onDone }: { pending: boolean; onDone: (kind: "reject" | "withdraw", note: string) => void }) {
  const [kind, setKind] = useState<"reject" | "withdraw" | null>(null);
  const [note, setNote] = useState("");
  if (!kind) {
    return (
      <div className="button-row spaced-top">
        <button type="button" className="link-btn danger" onClick={() => setKind("reject")}>
          Reject
        </button>
        <button type="button" className="link-btn" onClick={() => setKind("withdraw")}>
          Candidate withdrew
        </button>
      </div>
    );
  }
  return (
    <form
      className="form narrow spaced-top"
      onSubmit={(e) => {
        e.preventDefault();
        onDone(kind, note.trim());
      }}
    >
      <label className="field">
        <span className="label">{kind === "reject" ? "Reason for rejecting (optional)" : "Why they withdrew (optional)"}</span>
        <input autoFocus maxLength={5000} value={note} onChange={(e) => setNote(e.target.value)} placeholder={kind === "reject" ? "e.g. salary expectations too high" : "e.g. accepted another offer"} />
      </label>
      <div className="button-row">
        <button className={kind === "reject" ? "btn" : "btn secondary"} disabled={pending}>
          {kind === "reject" ? "Reject" : "Record withdrawal"}
        </button>
        <button type="button" className="link-btn" onClick={() => setKind(null)}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function Feedback({ onAdd, pending }: { onAdd: (note: string) => void; pending: boolean }) {
  const [note, setNote] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!note.trim()) return;
    onAdd(note.trim());
    setNote("");
  };
  return (
    <section className="section" aria-labelledby="feedback-h">
      <div className="section-head">
        <h2 id="feedback-h" className="label">
          Feedback
        </h2>
      </div>
      <form className="form narrow" onSubmit={submit}>
        <label className="field">
          <span className="sr-only">Feedback</span>
          <textarea rows={3} maxLength={5000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Interview feedback, concerns, next steps…" />
        </label>
        <div className="button-row">
          <button className="btn secondary" disabled={pending || !note.trim()}>
            Add feedback
          </button>
        </div>
      </form>
    </section>
  );
}

function PlaceForm({ onPlace, pending }: { onPlace: (a: ApplicationAction) => void; pending: boolean }) {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(todayInLondon());
  const [salary, setSalary] = useState("");
  const [markJobFilled, setMarkJobFilled] = useState(true);
  const { error, setError } = useAction();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = salary.replace(/[£€$,\s]/g, "");
    const value = /k$/i.test(text) ? Number(text.slice(0, -1)) * 1000 : Number(text);
    if (!Number.isInteger(value) || value <= 0) return setError("Enter the agreed salary as a whole number, e.g. 95000 or 95k.");
    setError(null);
    onPlace({ kind: "place", startDate, salary: value, markJobFilled });
  };

  return (
    <section className="section" aria-labelledby="place-h">
      <div className="section-head">
        <h2 id="place-h" className="label">
          Offer accepted?
        </h2>
        {!open && (
          <button type="button" className="link-btn" onClick={() => setOpen(true)}>
            Record placement
          </button>
        )}
      </div>
      {open && (
        <form className="form panel" onSubmit={submit}>
          <div className="grid-2">
            <Field label="Start date" type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <Field label="Agreed salary" inputMode="numeric" required value={salary} onChange={(e) => setSalary(e.target.value)} placeholder="95000 or 95k" />
          </div>
          <label className="check">
            <input type="checkbox" checked={markJobFilled} onChange={(e) => setMarkJobFilled(e.target.checked)} />
            <span>Mark the job as filled</span>
          </label>
          <p className="hint">The fee is taken from the job and worked out from this salary.</p>
          <ErrorNotice error={error} />
          <div className="button-row">
            <button className="btn" disabled={pending}>
              Record placement
            </button>
            <button type="button" className="link-btn" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
